import React, { useState, useMemo, useRef, useEffect } from "react";
import { useQuery, useMutation } from "@apollo/client";
import styled, { css, keyframes } from "styled-components";
import {
  GET_AI_SEARCH_RESULTS,
  CREATE_QUERY_REQUEST,
} from "../../component/ai-search/aisearch.gql.js";

import { AiSearchResultCard } from "../../component/ai-search/AiSearchResultCard";
import { TopMenu } from "../../component/_molecule/TopMenu";
import { LandingSection } from "../../component/ai-search/LandingSection";
import { ActionStepsSection } from "../../component/ai-search/ActionStepsSection";

const PageContainer = styled.div`
  padding: 15px;
  max-width: 400px;
  margin: 0 auto;
  background-color: #f9f9f9;
  min-height: 100vh;

  @media (max-width: 359px) {
    padding: 10px;
  }
`;

const Title = styled.h2`
  text-align: center;
  margin-bottom: 15px;
  color: #333;
  font-size: 1.3em;
`;

const Loading = styled.p`
  text-align: center;
  color: #888;
  padding-top: 50px;
`;

const ErrorMessage = styled.p`
  text-align: center;
  color: red;
  background-color: #ffebee;
  padding: 10px;
  border-radius: 4px;
  margin: 20px;
`;

const CardsGridContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 15px;
`;

const ResultsWrapper = styled.div`
  margin-top: 20px;
  min-height: 450px;
  position: relative;
`;

const goldAccent = "#a98e5d";
const chipBg = "#444444";
const chipBorder = "#666666";
const chipText = "#f0f0f0";
const chipHoverBg = "#555555";

const CategoryChipContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: 25px;
  padding: 15px 0;
`;

const CategoryChip = styled.button`
  padding: 8px 15px;
  border-radius: 16px;
  border: 1px solid ${chipBorder};
  background-color: ${chipBg};
  color: ${chipText};
  font-size: 0.9em;
  cursor: pointer;
  transition: background-color 0.2s, color 0.2s, border-color 0.2s;

  &:hover {
    background-color: ${chipHoverBg};
    border-color: ${chipHoverBg};
  }

  ${(props) =>
    props.isSelected &&
    css`
      background-color: ${goldAccent};
      color: ${props.theme?.colors?.textOnAccent || "#ffffff"};
      border-color: ${goldAccent};
      font-weight: bold;
    `}
`;

const SearchInputContainer = styled.div`
  margin-bottom: 20px;
  padding: 0 10px;
  position: relative;
`;

const SearchInput = styled.input`
  width: calc(100% - 22px);
  padding: 12px 10px;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-size: 1em;
  box-shadow: inset 0 1px 2px rgba(0, 0, 0, 0.1);

  &:focus {
    border-color: ${goldAccent};
    outline: none;
    box-shadow: 0 0 0 2px rgba(169, 142, 93, 0.2);
  }
`;

const AutocompleteDropdown = styled.ul`
  position: absolute;
  top: 100%;
  left: 10px;
  right: 10px;
  background-color: white;
  border: 1px solid #ccc;
  border-top: none;
  border-radius: 0 0 4px 4px;
  list-style: none;
  padding: 0;
  margin: 0;
  max-height: 200px;
  overflow-y: auto;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  z-index: 10;
`;

const SuggestionItem = styled.li`
  padding: 10px 12px;
  cursor: pointer;
  font-size: 0.95em;

  &:hover {
    background-color: #f0f0f0;
  }
`;

const darkGray = "#222222";
const lightGrayText = "#f0f0f0";
const subtleGrayText = "#bbbbbb";
const inputBgDark = "#333333";
const inputBorderDark = "#555555";

const FloatingContainer = styled.div`
  position: fixed;
  bottom: 30px;
  left: 50%;
  transform: translateX(-50%);
  background-color: ${goldAccent};
  color: white;
  border-radius: 25px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.25);
  padding: 0 25px;
  height: 50px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease-in-out;
  z-index: 900;
  overflow: hidden;
  width: auto;

  ${(props) =>
    (props.$state === "keyword" ||
      props.$state === "contact" ||
      props.$state === "error") &&
    css`
      cursor: default;
      width: calc(100% - 40px);
      max-width: 560px;
      height: auto;
      min-height: 170px;
      border-radius: 12px;
      padding: 30px 25px 25px 25px;
      flex-direction: column;
      align-items: stretch;
      background-color: ${darkGray};
      color: ${lightGrayText};
      bottom: 20px;
    `}

  ${(props) =>
    (props.$state === "loading" || props.$state === "success") &&
    css`
      cursor: default;
      min-width: 180px;
      height: 50px;
      padding: 0 30px;
      flex-direction: row;
      align-items: center;
      justify-content: center;
      background-color: #4caf50;
      color: white;
    `}
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  margin-bottom: 15px;
  position: relative;
  min-height: 25px;
`;

const BackButton = styled.button`
  background: none;
  border: none;
  color: ${subtleGrayText};
  font-size: 1.6em;
  cursor: pointer;
  padding: 0 10px 0 0;
  line-height: 1;
  height: 100%;

  &:hover {
    color: ${lightGrayText};
  }
`;

const FloatingButtonText = styled.span`
  font-size: 0.9em;
  font-weight: bold;
  white-space: nowrap;
`;

const FloatingFormLabel = styled.label`
  display: block;
  font-weight: bold;
  color: ${subtleGrayText};
  font-size: 0.9em;
  flex: 1;
  line-height: 1.3;
  padding-left: 5px;
`;

const FloatingInput = styled.input`
  width: calc(100% - 22px);
  padding: 10px 10px;
  border: 1px solid ${inputBorderDark};
  border-radius: 4px;
  margin-bottom: 12px;
  font-size: 0.9em;
  background-color: ${inputBgDark};
  color: ${lightGrayText};

  &::placeholder {
    color: ${subtleGrayText};
    opacity: 0.7;
  }

  &:focus {
    border-color: ${goldAccent};
    outline: none;
    background-color: #444;
  }
`;

const FloatingButton = styled.button`
  padding: 10px 18px;
  background-color: ${goldAccent};
  color: white;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  transition: background-color 0.2s, box-shadow 0.2s, transform 0.1s;
  font-size: 0.9em;
  font-weight: 600;
  width: 100%;
  margin-top: 12px;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.15);

  &:hover {
    background-color: #997f53;
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
  }

  &:active {
    transform: scale(0.98);
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  }

  &:disabled {
    background-color: #ccc;
    color: #666;
    box-shadow: none;
    cursor: not-allowed;
  }
`;

const FloatingMessage = styled.p`
  margin-bottom: 10px;
`;

const toastFadeInOut = keyframes`
  0% { opacity: 0; transform: translate(-50%, 20px); }
  10% { opacity: 1; transform: translate(-50%, 0); }
  90% { opacity: 1; transform: translate(-50%, 0); }
  100% { opacity: 0; transform: translate(-50%, -20px); }
`;

const ToastMessage = styled.div`
  position: fixed;
  bottom: 100px;
  left: 50%;
  transform: translateX(-50%);
  background-color: rgba(0, 0, 0, 0.7);
  color: white;
  padding: 12px 20px;
  border-radius: 20px;
  font-size: 0.9em;
  z-index: 1001;
  animation: ${toastFadeInOut} 4s ease-in-out forwards;
  text-align: center;
  white-space: pre-wrap;
`;


// 1. 요청된 7개 카테고리 목록 정의 (+ "전체")
const DEFINED_CATEGORIES = [
  "전체",
  "화장품",
  "향수/바디/헤어",
  "가방/지갑",
  "시계/주얼리",
  "패션",
  "주류",
  "기타",
];

export const AiSearchPage = () => {
  const [floatingFormState, setFloatingFormState] = useState("button");
  const [inputKeyword, setInputKeyword] = useState("");
  const [inputContact, setInputContact] = useState("");
  const [floatingError, setFloatingError] = useState("");
  const [toastMessage, setToastMessage] = useState("");

  const [selectedCategory, setSelectedCategory] = useState("전체");
  const [searchTerm, setSearchTerm] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [isInputFocused, setIsInputFocused] = useState(false);
  const resultsRef = useRef(null);

  // Initial data fetching
  const {
    loading: initialLoading,
    error: initialError,
    data: initialData,
  } = useQuery(GET_AI_SEARCH_RESULTS, {
    variables: { page: 1, limit: 1000 }, // Fetches all data upfront
  });

  const [submitRequest, { loading: mutationLoading }] = useMutation(
    CREATE_QUERY_REQUEST,
    {
      onCompleted: (data) => {
        console.log("Query request created:", data);
        commonSubmitActions();
      },
      onError: (error) => {
        console.error("Query request error:", error);
        setToastMessage(`오류가 발생했습니다: ${error.message}`);
        setTimeout(() => setToastMessage(""), 4000);
        setFloatingFormState("button");
        setInputKeyword("");
        setInputContact("");
        setFloatingError("");
      },
    }
  );

  const { processedItems: initialProcessedItems, uniqueCategories } =
    useMemo(() => {
      const itemsToProcess = initialData?.listAiProducts?.items || [];
      return {
        processedItems: itemsToProcess,
        uniqueCategories: DEFINED_CATEGORIES,
      };
    }, [initialData]);

  const filteredInitialItems = useMemo(() => {
    let items = initialProcessedItems;

    // 1. Filter by selectedCategory
    if (selectedCategory !== "전체") {
      if (selectedCategory === "기타") {
        const knownCategories = DEFINED_CATEGORIES.slice(
          1,
          DEFINED_CATEGORIES.length - 1
        );
        items = items.filter(
          (item) => !item.category || !knownCategories.includes(item.category)
        );
      } else {
        items = items.filter((item) => item.category === selectedCategory);
      }
    }

    // 2. Filter by searchTerm
    if (searchTerm.trim() !== "") {
      const lowerCaseSearchTerm = searchTerm.toLowerCase();
      items = items.filter((item) =>
        item.standardized_name?.toLowerCase().includes(lowerCaseSearchTerm)
      );
    }
    return items;
  }, [initialProcessedItems, selectedCategory, searchTerm]);

  const handleFloatingButtonClick = () => {
    if (floatingFormState === "button") {
      setFloatingFormState("keyword");
      setFloatingError("");
    }
  };

  const handleKeywordNext = () => {
    if (!inputKeyword.trim()) {
      setFloatingError("상품 키워드를 입력해주세요.");
      return;
    }
    setFloatingError("");
    setFloatingFormState("contact");
  };

  const commonSubmitActions = () => {
    setToastMessage("요청이 등록되었습니다.\n5분 후 확인해주세요.");
    setTimeout(() => setToastMessage(""), 4000);
    setFloatingFormState("button");
    setInputKeyword("");
    setInputContact("");
    setFloatingError("");
  };

  const handleFinalSubmitWithEmail = () => {
    const keyword = inputKeyword.trim();
    const email = inputContact.trim();
    if (!keyword) {
      setFloatingError("상품 키워드를 입력해주세요.");
      return;
    }
    if (email && !/\S+@\S+\.\S+/.test(email)) {
      setFloatingError("올바른 이메일 형식을 입력해주세요.");
      return;
    }
    setFloatingError("");
    submitRequest({
      variables: {
        createQueryRequestInput: {
          query_text: keyword,
          email: email || null,
        },
      },
    });
  };

  const handleGoBack = (e) => {
    e.stopPropagation();
    setFloatingError("");
    if (floatingFormState === "contact") {
      setFloatingFormState("keyword");
    } else if (floatingFormState === "keyword") {
      setFloatingFormState("button");
      setInputKeyword("");
      setInputContact("");
    }
  };

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value); // Directly update searchTerm, filtering happens in filteredInitialItems

    if (value.trim() === "") {
      setSuggestions([]);
    } else {
      const lowerCaseValue = value.toLowerCase();
      // Autocomplete can still work on initialProcessedItems (all data)
      const filteredSuggestions = initialProcessedItems
        .filter((item) =>
          item.standardized_name?.toLowerCase().includes(lowerCaseValue)
        )
        .slice(0, 7);
      setSuggestions(filteredSuggestions);
    }
  };

  const handleFocus = () => {
    setIsInputFocused(true);
    if (searchTerm.trim() !== "") {
      const lowerCaseValue = searchTerm.toLowerCase();
      const filteredSuggestions = initialProcessedItems
        .filter((item) =>
          item.standardized_name?.toLowerCase().includes(lowerCaseValue)
        )
        .slice(0, 7);
      setSuggestions(filteredSuggestions);
    }
  };

  const handleBlur = () => {
    setTimeout(() => {
      setIsInputFocused(false);
    }, 150);
  };

  const handleSuggestionClick = (suggestion) => {
    setSearchTerm(suggestion.standardized_name);
    setSuggestions([]);
    setIsInputFocused(false);
    // Scrolling to results logic removed as it was tied to async keyword search
  };

  // Determine which items, loading state, and error to display
  const displayItems = filteredInitialItems; // Always use filteredInitialItems
  const currentLoading = initialLoading; // Always use initialLoading
  const currentErrorToDisplay = initialError; // Always use initialError

  return (
    <PageContainer>
      <TopMenu title="AI 면세점 최저가 검색" />
      <LandingSection />
      <ActionStepsSection />
      <Title>AI 검색 결과</Title>
      <SearchInputContainer>
        <SearchInput
          type="text"
          placeholder="결과 내에서 상품 이름 검색..."
          value={searchTerm}
          onChange={handleSearchChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          autoComplete="off"
        />
        {isInputFocused && suggestions.length > 0 && (
          <AutocompleteDropdown>
            {suggestions.map((suggestion) => (
              <SuggestionItem
                key={suggestion.id} // Ensure suggestions have unique IDs
                onMouseDown={() => handleSuggestionClick(suggestion)}
              >
                {suggestion.standardized_name}
              </SuggestionItem>
            ))}
          </AutocompleteDropdown>
        )}
      </SearchInputContainer>

      {/* Category filters always visible */}
      {uniqueCategories.length > 1 && (
        <CategoryChipContainer>
          {uniqueCategories.map((category) => (
            <CategoryChip
              key={category}
              isSelected={selectedCategory === category}
              onClick={() => {
                setSelectedCategory(category);
                // searchTerm remains, filteredInitialItems will re-calculate
              }}
            >
              {category}
            </CategoryChip>
          ))}
        </CategoryChipContainer>
      )}

      <ResultsWrapper ref={resultsRef}>
        {currentLoading && <Loading>결과를 불러오는 중...</Loading>}
        {currentErrorToDisplay && (
          <ErrorMessage>
            오류가 발생했습니다: {currentErrorToDisplay.message}
          </ErrorMessage>
        )}
        {!currentLoading && !currentErrorToDisplay && (
          <>
            <CardsGridContainer>
              {Array.isArray(displayItems) && displayItems.length === 0 && (
                <p style={{ gridColumn: "1 / -1", textAlign: "center" }}>
                  {searchTerm.trim() !== ""
                    ? `"${searchTerm}"에 대한 검색 결과가 없습니다${
                        selectedCategory !== "전체"
                          ? ` ('${selectedCategory}' 카테고리)`
                          : ""
                      }.`
                    : selectedCategory !== "전체"
                    ? `'${selectedCategory}' 카테고리에 상품이 없습니다.`
                    : "검색 결과가 없습니다."}
                </p>
              )}
              {Array.isArray(displayItems) &&
                displayItems.map((item) => (
                  <AiSearchResultCard key={item.id} item={item} />
                ))}
            </CardsGridContainer>
          </>
        )}
      </ResultsWrapper>

      {toastMessage && <ToastMessage>{toastMessage}</ToastMessage>}

      <FloatingContainer
        $state={floatingFormState}
        onClick={handleFloatingButtonClick}
      >
        {floatingFormState === "button" && (
          <FloatingButtonText>원하시는 상품이 없으신가요?</FloatingButtonText>
        )}
        {floatingFormState === "keyword" && (
          <>
            <HeaderRow>
              <BackButton onClick={handleGoBack}>&#8249;</BackButton>
              <FloatingFormLabel htmlFor="floatKeywordInput">
                1. 상품 키워드를 입력해주세요
              </FloatingFormLabel>
            </HeaderRow>
            <FloatingInput
              id="floatKeywordInput"
              type="text"
              placeholder="예: 샤넬 클래식 플랩백 미디움"
              value={inputKeyword}
              onChange={(e) => setInputKeyword(e.target.value)}
              onClick={(e) => e.stopPropagation()}
              autoFocus
            />
            {floatingError && (
              <FloatingMessage isError={true}>{floatingError}</FloatingMessage>
            )}
            <FloatingButton
              onClick={(e) => {
                e.stopPropagation();
                handleKeywordNext();
              }}
              disabled={!inputKeyword.trim()}
            >
              다음
            </FloatingButton>
          </>
        )}
        {floatingFormState === "contact" && (
          <>
            <HeaderRow>
              <BackButton onClick={handleGoBack}>&#8249;</BackButton>
              <FloatingFormLabel htmlFor="floatContactInput">
                2. 알림 받으실 이메일 (선택)
              </FloatingFormLabel>
            </HeaderRow>
            <FloatingInput
              id="floatContactInput"
              type="email"
              placeholder="알림을 원하시면 이메일을 입력하세요"
              value={inputContact}
              onChange={(e) => setInputContact(e.target.value)}
              onClick={(e) => e.stopPropagation()}
              autoFocus
            />
            {floatingError && (
              <FloatingMessage isError={true}>{floatingError}</FloatingMessage>
            )}
            <FloatingButton
              disabled={mutationLoading}
              onClick={(e) => {
                e.stopPropagation();
                handleFinalSubmitWithEmail();
              }}
            >
              {mutationLoading ? "요청 중..." : "최저가 요청하기"}
            </FloatingButton>
          </>
        )}
      </FloatingContainer>
    </PageContainer>
  );
};
